import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";

/**
 * LMS - Quick Test / Practice Test (self-check, nothing stored).
 *
 * 10 random questions for a subject, no timer, no pass mark. On submit the score
 * and the right/wrong answers are shown so the student can learn. Closing or
 * reopening starts fresh - nothing is recorded anywhere.
 *
 * props: subjectId, subjectName, mode ("practice" | "quick"), show, onClose
 */
export default function PracticeTest({ subjectId, subjectName, mode, show, onClose }) {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("studentToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // question_id -> mcq_id
  const [result, setResult] = useState(null); // { correct,total,percent,results }

  useEffect(() => {
    if (show && subjectId) load();
    if (!show) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, subjectId, mode]);

  const reset = () => {
    setQuestions([]);
    setAnswers({});
    setResult(null);
  };

  const load = async () => {
    reset();
    setLoading(true);
    try {
      const res = await dispatch(getDataAPI(`student/lms/practice/${subjectId}?mode=${mode}`, token()));
      setQuestions(res.data.data.questions || []);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not load the test." });
      onClose();
    }
    setLoading(false);
  };

  const pick = (qid, mcqId) => {
    if (result) return; // locked after submit
    setAnswers((a) => ({ ...a, [qid]: mcqId }));
  };

  const submit = async () => {
    try {
      const res = await dispatch(postDataAPI("student/lms/practice/check", { answers }, token()));
      setResult(res.data.data);
    } catch (err) {
      alert({ error: "Could not check your answers." });
    }
  };

  const verdict = (qid) => {
    if (!result) return null;
    return result.results.find((r) => r.question_id === qid);
  };

  const optStyle = (qid, optId) => {
    const base = { display: "block", padding: "8px 12px", marginBottom: 6, borderRadius: 6, border: "1px solid #ccc", cursor: result ? "default" : "pointer" };
    if (!result) {
      return answers[qid] === optId ? { ...base, border: "1px solid #0d6efd", background: "#eaf2ff" } : base;
    }
    const v = verdict(qid);
    if (v && v.correct_mcq_id === optId) return { ...base, border: "1px solid #28a745", background: "#e7f6ec" };
    if (v && v.chosen_mcq_id === optId && !v.is_correct) return { ...base, border: "1px solid #dc3545", background: "#fbeaec" };
    return base;
  };

  const answeredCount = Object.keys(answers).length;

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 17 }}>
          <span
            style={{
              background: mode === "quick" ? "#198754" : "#6c757d",
              color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
              padding: "3px 9px", borderRadius: 20, marginRight: 9, verticalAlign: "middle",
            }}
          >
            {mode === "quick" ? "QUICK TEST" : "PRACTICE"}
          </span>
          {subjectName || "Self-check"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#f6f7f9" }}>
        {loading ? (
          <div className="text-center text-muted" style={{ padding: 40 }}>Loading questions…</div>
        ) : questions.length === 0 ? (
          <div className="text-center text-muted" style={{ padding: 40 }}>
            No questions available for this subject yet.
          </div>
        ) : (
          <>
            {result && (
              <div
                style={{
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                  padding: "16px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16,
                }}
              >
                <div
                  style={{
                    width: 64, height: 64, borderRadius: "50%", flex: "0 0 64px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 17, color: "#fff",
                    background: result.percent >= 40 ? "#198754" : "#dc3545",
                  }}
                >
                  {result.percent}%
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>Score: {result.correct} / {result.total}</div>
                  <div className="text-muted" style={{ fontSize: 12.5 }}>
                    Green = correct answer, red = your wrong choice. Self-check only — nothing is saved.
                  </div>
                </div>
              </div>
            )}
            {questions.map((q, i) => (
              <div
                key={q.question_id}
                style={{
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                  padding: "14px 16px", marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <span
                    style={{
                      flex: "0 0 26px", width: 26, height: 26, borderRadius: "50%",
                      background: "#243386", color: "#fff", display: "flex",
                      alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </span>
                  <div style={{ fontWeight: 600, lineHeight: 1.4 }}>{q.question}</div>
                </div>
                <div style={{ paddingLeft: 36 }}>
                  {q.options.map((o) => (
                    <label key={o.id} style={optStyle(q.question_id, o.id)}>
                      <input
                        type="radio"
                        name={`pq${q.question_id}`}
                        checked={answers[q.question_id] === o.id}
                        onChange={() => pick(q.question_id, o.id)}
                        disabled={!!result}
                        style={{ marginRight: 8 }}
                      />
                      {o.text}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <small className="text-muted">
          {questions.length > 0 && !result
            ? `${answeredCount} of ${questions.length} answered`
            : result
            ? "Completed"
            : ""}
        </small>
        <div>
          {!result && questions.length > 0 && (
            <Button variant="primary" onClick={submit} disabled={answeredCount === 0}>
              Submit
            </Button>
          )}
          {result && (
            <Button variant="outline-primary" className="mr-2" onClick={load}>
              Try again
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
